import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WebcamCapture from '../components/WebcamCapture';
import UploadImage from '../components/UploadImage';
import TabSelector from '../components/TabSelector';
import FaceShapeInfo from '../components/FaceShapeInfo';
import { analyzeLocalImage } from '../utils/api';
import { loadModels } from '../utils/faceDetectionUtils';

const HomeScreen = ({ onAnalysisStart, onAnalysisComplete, onAnalysisError }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [image, setImage] = useState(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const navigate = useNavigate();
  
  // Load TensorFlow.js models on component mount
  useEffect(() => {
    const initModels = async () => {
      try {
        await loadModels();
        setModelLoaded(true);
      } catch (error) {
        console.error('Error loading models:', error);
        if (onAnalysisError) {
          onAnalysisError('Failed to load face detection models. Please refresh the page and try again.');
        }
      }
    };
    
    initModels();
  }, [onAnalysisError]);
  
  // Tabs configuration
  const tabs = [
    { id: 'upload', name: 'Upload Photo' },
    { id: 'webcam', name: 'Use Webcam' }
  ];
  
  // Handle image capture from webcam
  const handleWebcamCapture = (imageData) => {
    setImage(imageData);
  };
  
  // Handle image upload
  const handleImageUpload = (imageData) => {
    setImage(imageData);
  };
  
  // Handle error messages
  const handleError = (errorMessage) => {
    if (onAnalysisError) {
      onAnalysisError(errorMessage);
    }
  };
  
  // Start face analysis when user clicks the button
  const startAnalysis = async () => {
    if (!image) {
      handleError('Please upload or capture an image first.');
      return;
    }
    
    if (!modelLoaded) {
      handleError('Face detection models are still loading. Please wait a moment and try again.');
      return;
    }
    
    try {
      // Notify parent that analysis is starting
      if (onAnalysisStart) {
        onAnalysisStart();
      }
      
      // Perform client-side analysis on the image
      const result = await analyzeLocalImage(image);
      
      if (result.success) {
        // If analysis is successful, pass results to parent and navigate to results page
        if (onAnalysisComplete) {
          onAnalysisComplete({
            imagePath: image, // Use the local image path
            faceShape: result.analysis.faceShape,
            description: result.analysis.description,
            recommendations: result.analysis.recommendations
          });
        }
        
        // Navigate to results page
        navigate('/results');
      } else {
        // Handle analysis error
        handleError(result.message || 'Failed to analyze face. Please try again.');
      }
    } catch (error) {
      console.error('Error during analysis:', error);
      handleError('An error occurred during analysis. Please try again.');
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Face Shape Analyzer
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Discover your face shape and get personalized style recommendations for glasses, 
          hairstyles, and clothing that complement your unique features.
        </p>
        {!modelLoaded && (
          <p className="mt-2 text-amber-600">
            Loading face detection models... Please wait.
          </p>
        )}
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <TabSelector 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
        
        <div className="p-6">
          {activeTab === 'upload' ? (
            <UploadImage 
              onUpload={handleImageUpload} 
              onError={handleError}
            />
          ) : (
            <WebcamCapture 
              onCapture={handleWebcamCapture} 
              onError={handleError}
            />
          )}
          
          {image && (
            <div className="mt-6 text-center">
              <button 
                onClick={startAnalysis}
                disabled={!modelLoaded}
                className={`btn-primary text-lg px-8 py-3 ${!modelLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Analyze Face Shape
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Click to analyze your face shape and get personalized recommendations
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* How it works section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6 text-center">How It Works</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 text-primary-600 mb-4">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Upload or Capture</h3>
            <p className="text-gray-600">
              Upload a photo or use your webcam to take a picture of your face looking straight at the camera.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 text-primary-600 mb-4">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">AI Analysis</h3>
            <p className="text-gray-600">
              Our AI analyzes your facial features to determine your face shape using advanced detection techniques.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 text-primary-600 mb-4">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v10m2 2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Get Recommendations</h3>
            <p className="text-gray-600">
              Receive personalized recommendations for glasses, hairstyles, and clothing colors that complement your face shape.
            </p>
          </div>
        </div>
      </div>
      
      {/* Face shape guide section */}
      <FaceShapeInfo />
      
      {/* Privacy notice */}
      <div className="mt-12 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Privacy Notice:</strong> Your photos are processed locally in your browser and are not stored on our servers. 
              We respect your privacy and do not collect any personal information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;